import React from 'react';
import { DivisionMileStone } from '../../../../../../api/divisionMileStones/types';
import { ChipColor, ChipSize, ChipVariant, TaskChipType, MilestoneChipData } from './types';
import { SimpleChip } from './SimpleChip';
import { TaskChip } from './TaskChip';
import { StatusChip } from './StatusChip';
import { MilestoneChips } from './MilestoneChips';

// Main BaseChip Component - Simplified
interface BaseChipProps {
     size?: ChipSize;
     sx?: any;
     variant?: ChipVariant;

     // Task chip props
     task?: DivisionMileStone;
     getTaskChipType?: (task: DivisionMileStone) => TaskChipType;
     getTaskCheckboxState?: (taskId: string) => boolean;

     // Milestone chip props
     milestone?: any;
     getMilestoneChipData?: (milestone: any) => MilestoneChipData | null;
     areAllTasksChecked?: boolean;
     hasMilestoneSyncProgress?: boolean;
     hasMilestoneUnsavedChanges?: boolean;

     // Status chip props
     hasDraftChanges?: boolean;
     hasUnsavedChanges?: boolean;
     workflowTasksCount?: number;
     allTasksReleased?: boolean;

     // Simple chip props
     label?: string;
     color?: ChipColor;
     customColor?: string;
}

export const BaseChip: React.FC<BaseChipProps> = React.memo(({
     size = 'small',
     sx,
     variant = 'filled',
     task,
     getTaskChipType,
     getTaskCheckboxState,
     milestone,
     getMilestoneChipData,
     areAllTasksChecked,
     hasMilestoneSyncProgress,
     hasMilestoneUnsavedChanges,
     hasDraftChanges,
     hasUnsavedChanges,
     workflowTasksCount,
     allTasksReleased,
     label,
     color,
     customColor,
}) => {
     // Milestone chips (highest priority)
     if (milestone && getMilestoneChipData && areAllTasksChecked !== undefined) {
          return (
               <MilestoneChips
                    milestone={milestone}
                    getMilestoneChipData={getMilestoneChipData}
                    areAllTasksChecked={areAllTasksChecked}
                    hasMilestoneSyncProgress={hasMilestoneSyncProgress}
                    hasMilestoneUnsavedChanges={hasMilestoneUnsavedChanges}
                    workflowTasksCount={workflowTasksCount}
                    size={size}
                    variant={variant}
                    sx={sx}
               />
          );
     }

     // Status chip
     if (hasDraftChanges !== undefined || hasUnsavedChanges !== undefined || workflowTasksCount !== undefined) {
          return (
               <StatusChip
                    hasDraftChanges={hasDraftChanges}
                    hasUnsavedChanges={hasUnsavedChanges}
                    workflowTasksCount={workflowTasksCount}
                    allTasksReleased={allTasksReleased}
                    size={size}
                    variant={variant}
                    sx={sx}
               />
          );
     }

     // Task chip
     if (task && getTaskChipType && getTaskCheckboxState) {
          return (
               <TaskChip
                    task={task}
                    getTaskChipType={getTaskChipType}
                    getTaskCheckboxState={getTaskCheckboxState}
                    size={size}
                    variant={variant}
                    sx={sx}
               />
          );
     }

     // Simple chip
     if (label) {
          return (
               <SimpleChip
                    label={label}
                    color={color}
                    size={size}
                    variant={variant}
                    sx={sx}
                    customColor={customColor}
               />
          );
     }

     return null;
});